import React, { useMemo } from "react";
import { Stack, useTheme } from "@mui/material";
import { alpha } from '@mui/material/styles';
import _ from "lodash";
import { useSelector } from "react-redux";
import { TopicChip as Chip } from '../../common/topicChip';
import { defaultGuid } from "../../../../common/utils";
import { promptsSelector } from "../../../../store/slices/prompts";
import AppConfig from "../../../../appConfig";

// Chip colour per environment: dev orange, test green, demo purple, prod blue.
const envChipColors = {
  dev: { main: '#ed6c02', dark: '#b53d00', light: '#ffb74d' },
  test: { main: '#2e7d32', dark: '#1b5e20', light: '#81c784' },
  testrmp: { main: '#2e7d32', dark: '#1b5e20', light: '#81c784' },
  demo: { main: '#7b1fa2', dark: '#4a148c', light: '#ba68c8' },
  prod: { main: '#0d6efd', dark: '#0a58ca', light: '#6ea8fe' },
  prodrmp: { main: '#0d6efd', dark: '#0a58ca', light: '#6ea8fe' },
};
const greyChipColor = { main: '#6c757d', dark: '#495057', light: '#ced4da' };

// Assignment chip styling. The chip for the selected environment is outlined in a darker shade of
// its own colour; in dark mode the border uses the base shade so it stays visible.
const getAssignmentChipSx = (theme, color, isArchived, isCurrentEnv) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    height: '20px',
    fontSize: '11px',
    fontWeight: isCurrentEnv ? 600 : 400,
    color: isArchived ? theme.palette.text.disabled : (isDark ? color.light : color.dark),
    backgroundColor: isArchived
      ? theme.palette.action.disabledBackground
      : alpha(color.main, isDark ? 0.25 : 0.14),
    border: `1px solid ${isArchived || !isCurrentEnv ? 'transparent' : (isDark ? color.main : color.dark)}`,
  };
};

export const getCompanyChipLabel = (companyEnv) =>
  `${companyEnv.label} · ${companyEnv.companies.length} ${companyEnv.companies.length === 1 ? 'Company' : 'Companies'}`;

// promptId_subType -> { envs: [{label, value}], companyEnvs: [{label, value, companies: [name]}] }.
// Default rows become env chips; company rows are grouped per environment (companies live in the env db).
export const useAssignmentsByPrompt = () => {
  const { envAssignments } = useSelector(promptsSelector);
  return useMemo(()=>{
    const grouped = {};
    _.forEach(envAssignments, (item)=>{
      const promptId = _.toLower(item?.promptId);
      if(!promptId) return;
      const key = promptId + '_' + item?.subType;
      if(!grouped[key]){
        grouped[key] = { envs: [], companies: {} };
      }
      const env = _.toLower(item?.env);
      if(item?.companyId === defaultGuid){
        grouped[key].envs.push(env);
      } else {
        if(!grouped[key].companies[env]){
          grouped[key].companies[env] = [];
        }
        grouped[key].companies[env].push(item?.companyName || 'Unknown company');
      }
    });
    return _.mapValues(grouped, (value)=>({
      envs: _.filter(AppConfig.allEnvsList, (env)=>_.includes(value.envs, env.value)),
      companyEnvs: _.map(_.filter(AppConfig.allEnvsList, (env)=>value.companies[env.value]), (env)=>({
        ...env,
        companies: _.sortBy(_.uniq(value.companies[env.value])),
      })),
    }));
  },[envAssignments]);
};

export const getPromptAssignment = (assignmentsByPrompt, prompt) =>
  assignmentsByPrompt[_.toLower(prompt?.promptId) + '_' + prompt?.subType];

// Env chips (default prompt in that environment), then one company chip per environment.
export const PromptAssignmentChips = ({ assignment, isArchived, sx }) => {
  const theme = useTheme();
  if (!(assignment?.envs?.length > 0 || assignment?.companyEnvs?.length > 0)) return null;
  return (
    <Stack direction={'row'} flexWrap={'wrap'} gap={0.5} sx={sx}>
      {_.map(assignment.envs, (env) => (
        <Chip
          key={env.value}
          tooltip
          tooltipText={`Default prompt in ${env.label}`}
          label={env.label}
          sx={getAssignmentChipSx(
            theme,
            envChipColors[env.value] ?? greyChipColor,
            isArchived,
            env.value === AppConfig.appMode
          )}
        />
      ))}
      {_.map(assignment.companyEnvs, (env) => (
        <Chip
          key={'company-' + env.value}
          tooltip
          tooltipText={`Company prompt in ${env.label}: ${env.companies.join(', ')}`}
          label={getCompanyChipLabel(env)}
          sx={getAssignmentChipSx(
            theme,
            greyChipColor,
            isArchived,
            env.value === AppConfig.appMode
          )}
        />
      ))}
    </Stack>
  );
};
